import {
  DISPLAY_CURRENCIES,
  ICON_TYPES,
  TIME_RANGES,
} from "@shared/common/constants";
import {
  ApiResponse,
  ClientData,
  ClientStatus,
  FixMeLater,
  GraphDataType,
  Limits,
  PrecisionConfig,
} from "@shared/types";
import { ValueOf } from "@shared/types/utils";
import useMainCoinsAvailableAmounts from "@shared/utils/hooks/useMainCoinsAvailableAmounts";
import { ReactNode } from "react";

export type TimeRange = (typeof TIME_RANGES)[number];
export type IconType = ValueOf<typeof ICON_TYPES>;
export type DisplayCurrency = keyof typeof DISPLAY_CURRENCIES;
export type DisplayCurrenciesToNumber = {
  [key in DisplayCurrency]?: number;
};
export type UnixTimestamp = number;
export type Selector<T> = (state: FixMeLater) => T;

export enum ApiTags {
  SUBGROUP_REVIEWS = "SubGroupReviews",
  WATCHLIST = "Watchlist",
  TRADES = "Trades",
  CLOSED_TRADES = "ClosedTrades",
  USER = "User",
  RECENT_PAYMENTS = "RecentPayments",
  TRADING_VIEW = "TradingView",
  SIGNALS = "Signals",
  CLIENTS = "Clients",
  ADMIN_SUB_GROUP_DATA = "AdminSubGroupData",
  TRADING_CONFIGURATION = "TradingConfiguration",
  ACCOUNT_CONFIGURATION = "AccountConfiguration",
  ACCOUNTS_AND_GROUPS = "AccountsAndGroups",
  AUTO_TRADING_GROUPS = "AutoTradingGroups",
  BOTS = "Bots",
  MAIN_COINS_AVAILABLE_AMOUNT = "MainCoinsAvailableAmount",
  AFFILIATION_REFERRAL_LINKS = "AffiliationReferralLinks",
  AFFILIATION_PAYOUT_METHODS = "AffiliationPayoutMethods",
  AFFILIATION_PAYOUTS = "AffiliationPayouts",
  GROUPS = "Groups",
  PRICE_CHARTS = "PriceCharts",
  PORTFOLIO_DATA = "PortfolioData",
  GROUP_DATA = "GroupData",
  SIGNALS_SUB_GROUPS = "SignalsSubGroups",
  CHANNEL_CONFIGURATION = "ChannelConfiguration",
  EXTERNAL_ACCOUNT_INVITES = "ExternalAccountInvites",
}

export enum SortingOrder {
  DESCEND = -1,
  ASCEND = 1,
}

interface Balance {
  amount?: number;
  quantity?: number;
}

export interface AvailableAmountResponse {
  coin: CoinName;
  limits: Pick<Limits, "amount">;
  data: {
    balance: Balance;
  };
  precision: PrecisionConfig;
}

export interface AvailableQuantityResponse {
  coin: CoinName;
  limits: Pick<Limits, "quantity">;
  amount_conversions: string;
  data: {
    balance: Balance;
  };
  precision: PrecisionConfig;
}

export interface AvailableIconAssetsResponse {
  data: CoinName[];
}

export interface SignalGraphRequest {
  exchange_signal_id: number;
  time_range: string;
  symbol?: string;
  for_admin: boolean;
}

export interface TradeGraphRequest {
  trade_id: number;
  time_range: TimeRange;
  ignore_status?: boolean;
}

export type PriceGraphDataRequest = SignalGraphRequest | TradeGraphRequest;

export interface PriceGraphData {
  graph_data: GraphDataType;
  price_coin: string;
}

export enum AvoPageName {
  BOT_FORM = "Bot Form",
  BOT_SUMMARY = "Bot Summary",
  BOT_EXTRA_DETAILS = "Bot Extra Details",
  ACCOUNT_CONFIGURATION = "Account Configuration",
  ACCOUNTS_AND_GROUPS = "Accounts and Groups",
  FILTERS = "Filters",
  AFFILIATION = "Affiliation",
  AFFILIATION_REFERRAL_LINKS = "Affiliation Referral Links",
  AFFILIATION_EXISTING_USERS = "Affiliation Existing Users",
  AFFILIATION_EARNINGS = "Affiliation Earnings",
  AFFILIATION_PAYOUTS = "Affiliation Payouts",
  CREATE_OR_EDIT_REFERRAL_LINK = "Create/Edit Referral Link",
  TRADES = "Trades",
  SIGNALS = "Signals",
  CONNECT_EXTERNAL_ACCOUNT = "Connect External Account",
  BILLING = "Billing",
  TRADING_TERMINAL = "Trading Terminal",
  CREATE_TRADE = "Create Trade",
  BACKTESTING = "Backtesting",
  MARKETPLACE = "Marketplace",
  ACCOUNT_AND_SECURITY = "Account and Security",
  ADMIN = "Admin",
}

export type ExchangeName = string;
export type AccountName = string;
export type CoinName = string;
export type ClientToExchangeMapping = { [key in AccountName]: ExchangeName };
export type ExchangeToClientsMapping = {
  [key in ExchangeName]: AccountName[] | undefined;
};
export type ExchangeToSymbolsMapping = { [key in ExchangeName]: CoinName[] };
export type SymbolToExchangesMapping = { [key in CoinName]: ExchangeName[] };

export interface GetAvailableQuantityOrAmountRequest {
  account_id: number;
  symbol: CoinName;
  reverse: boolean;
}

export interface SymbolMetadata {
  all_equivalent_symbols: string[];
  main_coin: string;
  limits?: Limits;
  amount_conversion_formula?: string;
  quantity_conversion_formula?: string;
  is_active: boolean;
  mica_is_active?: boolean;
}

export interface SymbolsMetadata {
  [exchangeName: string]: {
    [symbol: string]: SymbolMetadata;
  };
}

export interface ExchangeMetadata {
  is_margin_exchange: boolean;
  is_spot_exchange: boolean;
  is_inverse_exchange: boolean;
  limits: {
    price_factor: { min: number; max: number };
  };
  mica_pairs?: string[];
  pairs?: string[];
}

export interface ExchangesMetadata {
  [exchangeName: string]: ExchangeMetadata;
}

export interface MainCoinAmountData {
  amount?: number;
  btc_amount: number;
  usd_amount: number;
}

export type getAccountsMainCoinsAvailableAmountRequest = void | {
  client_id?: number;
  client_ids?: number[];
};

export interface getAccountsMainCoinsTotalAndAvailableAmountRequest {
  return_total?: boolean;
  return_client_amounts?: boolean;
  client_id?: number;
  client_ids?: number[];
}

export type MainCoinsAvailableAmountData = {
  [accountID: number]: {
    [mainCoinName: string]: MainCoinAmountData;
  };
};

export type MainCoinsTotalAndAvailableAmountData = {
  client: {
    [accountID: number]: Record<
      GeneralAmountType,
      { USD: number; BTC: number }
    >;
  };
  main_coin: Record<GeneralAmountType, MainCoinsAvailableAmountData>;
};

export enum GeneralAmountType {
  TOTAL = "total",
  AVAILABLE = "available",
}

export type MainCoinsAvailableAmountsData = ReturnType<
  ReturnType<typeof useMainCoinsAvailableAmounts>["getMainCoinsAmountsData"]
>;

export interface MutationStatus<Data = any, Args = any> {
  data?: Data;
  currentData?: Data;
  isUninitialized: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  endpointName?: string | undefined;
  originalArgs?: Args | void;
  error?: { data: ApiResponse<Data>; status: number };
}

export interface LazyQueryStatus<Data = any, Args = any>
  extends MutationStatus<Data, Args> {
  isFetching: boolean;
}

export interface QueryStatus<Data = any, Args = any>
  extends LazyQueryStatus<Data, Args> {
  refetch: () => void;
}

export enum Direction {
  LONG = "Long",
  SHORT = "Short",
}

export enum StopType {
  LIMIT = 1,
  MARKET = 2,
}

export enum EnvType {
  DEMO = 1, // None is normal env
}

export enum EnvironmentType {
  PROD = "prod",
  STAGING = "staging",
  TEST = "test",
  DEV = "dev",
  LOCAL = "local",
}

export enum RequestSource {
  WEBSITE = "website",
  APP = "app",
}

export enum Platform {
  TELEGRAM_BOT = 1,
  MOBILE_APP = 2,
  WEBSITE = 3,
}

export enum PlatformType {
  TELEGRAM = 1,
  DISCORD = 2,
  WEBSITE = 3,
  MOBILE = 4,
}

export type OpenMessage = (
  text: string,
  type: MessageType,
  duration?: number
) => void;

export type MessageType = "error" | "success" | "warning" | "info";

export type DisplayResultMessage = (
  promise: Promise<any>,
  successMessage?: string,
  defaultError?: string,
  duration?: number,
  onSuccessCallback?: (response: any) => void,
  ignoreStatuses?: number[],
  showSuccess?: boolean,
  useSuccessMessage?: boolean
) => Promise<any>;

export type OnlyHelperTooltipProps = {
  avoFieldName?: string;
  avoPageName?: string;
  description?: ReactNode;
  articleLink?: string;
  articleId?: string;
};

export enum ConfigurationSections {
  GENERAL = "General",
  TRAILING = "Trailing",
  STRATEGIES = "Strategies",
  STOP_LOSS = "Stop-loss",
  AUTO_TRADING_FILTERS = "Auto-Trading Filters",
  MARGIN = "Margin",
}

export interface StoreNpsResultRequest {
  rating?: number;
  text?: string;
  platform_type: PlatformType;
}

export type OpenConfirmModal<T> = (
  confirmStr: ReactNode | string[],
  handleOk: () => Promise<void> | void,
  okType?: T | undefined,
  hideCancel?: boolean,
  okText?: string,
  cancelText?: string,
  handleCancel?: () => Promise<void> | void
) => void;

export enum TimeUnit {
  YEAR = "Year",
  DAY = "Day",
  HOUR = "Hour",
  MINUTE = "Minute",
  SECOND = "Second",
}

export enum WorkspaceType {
  ADMIN = "Admin",
  USER = "User",
  DEMO = "Demo",
}

export enum PrecisionType {
  QUANTITY = "quantity",
  AMOUNT = "amount",
  PRICE = "price",
  PERCENT = "percent",
  BALANCE = "balance",
  CHANGE = "change",
  USD_AMOUNT = "usd_amount",
  BTC_AMOUNT = "btc_amount",
  USD_PRICE = "usd_price",
  BTC_PRICE = "btc_price",
  USD_BALANCE = "usd_balance",
  BTC_BALANCE = "btc_balance",
}

export enum UtmParams {
  UTM_ID = "utm_id",
  UTM_SOURCE = "utm_source",
  UTM_MEDIUM = "utm_medium",
  GCLID = "gclid",
}

export interface GetMinimumTimestampRequest {
  exchange: string;
  symbol?: string;
  include_site_db?: boolean;
}

export interface Inset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export type DisplayCurrencyValue = { USD: number; BTC: number };

export interface SymbolsMetadataManager {
  symbolsMetadata: SymbolsMetadata | undefined;
  exchangeToSymbols: Record<string, Set<string>> | undefined;
  getSymbolExchanges: (symbol?: string) => string[];
  getExchangeToSymbolsStatus: QueryStatus<SymbolsMetadata>;
  isSymbolActiveInAccount: (
    symbol?: string,
    accountId?: number
  ) => boolean | undefined;
  getSymbolMetadata: (
    symbol?: string,
    exchange?: string
  ) => SymbolMetadata | undefined;
  getMainCoin: (symbol?: string, exchange?: string) => string | undefined;
  getConversionFormula: (
    symbol?: string,
    exchange?: string
  ) => string | undefined;
  getQuantityCoin: (
    symbol?: string,
    exchange?: string,
    isLeftSideOnly?: boolean
  ) => string | undefined;
  getExchangeSymbols: (exchange?: string | undefined) => string[];
  getAccountActiveSymbols: (
    clientId?: number | undefined
  ) => string[] | undefined;
  isSuccess: boolean;
}

export interface ClientsMetadataManager {
  getClientsListStatus: QueryStatus<ClientData[]>;
  getAccountByName: (accountName: string) => ClientData | undefined;
  getAccountById: (accountId?: number) => ClientData | undefined;
  clientExchanges: string[];
  clientIds: number[];
  getAccountExchangeById: (accountId: number) => string | undefined;
  clientsData: ClientData[] | undefined;
  activeClientIds: number[];
  activeClientsData: ClientData[] | undefined;
  notDeletedClientsData: ClientData[] | undefined;
  getIsAccountInvalid: (accountId?: number) => boolean | undefined;
  getIsAccountInactive: (accountId?: number) => boolean | undefined;
  getAccountStatus: (accountId?: number) => ClientStatus | undefined;
  getAccountHasActiveDuplicate: (accountId?: number) => boolean;
  getIsMicaAccount: (accountId?: number) => boolean;
  getActiveAccountsWithSameApis: (
    accountId?: number
  ) => ClientData[] | undefined;
  isSuccess?: boolean;
}
